import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { PAGE_META, DEFAULT_META, PageMeta } from './page-meta.config';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly baseUrl = 'https://alqaryahauction.com';
  private readonly ogImage = 'https://alqaryahauction.com/assets/images/download.svg';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  update(url: string, isArabic: boolean): void {
    const cleanUrl = isArabic
      ? (url.startsWith('/ar') ? url.substring(3) || '/' : url)
      : url;

    if (isPlatformBrowser(this.platformId)) {
      this.doc.documentElement.lang = isArabic ? 'ar' : 'en';
    }

    const meta = this.resolveMeta(cleanUrl, isArabic);

    this.titleService.setTitle(meta.title);
    this.metaService.updateTag({ name: 'description', content: meta.description });
    this.metaService.updateTag({ name: 'keywords', content: meta.keywords });

    this.setCanonicalUrl(url);
    this.setHrefLangLinks(cleanUrl);
    this.setOpenGraphTags(meta.title, meta.description, url);
  }

  // Inject JSON-LD structured data into <head> — works on both server and browser
  setStructuredData(schemas: object[]): void {
    this.doc.querySelectorAll('script[type="application/ld+json"][data-seo-dynamic]')
      .forEach(el => el.remove());

    schemas.forEach(schema => {
      const script = this.doc.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-dynamic', '');
      script.textContent = JSON.stringify(schema);
      this.doc.head.appendChild(script);
    });
  }

  private resolveMeta(path: string, isArabic: boolean): PageMeta {
    const lang = isArabic ? 'ar' : 'en';
    return PAGE_META[path]?.[lang] ?? DEFAULT_META[lang];
  }

  // Runs on both server (SSR) and browser via DOCUMENT injection
  private setCanonicalUrl(url: string): void {
    let link: HTMLLinkElement = this.doc.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    const isHome = url === '/' || url === '' || url === '/home' || url.startsWith('/home?');
    link.setAttribute('href', isHome ? this.baseUrl : this.baseUrl + url);
  }

  // Browser-only: avoid duplicate link tags during SSR hydration
  private setHrefLangLinks(cleanUrl: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.doc.querySelectorAll("link[rel='alternate'][hreflang]").forEach(l => l.remove());

    const path = cleanUrl === '/' ? '' : cleanUrl;
    [
      { hreflang: 'en', href: this.baseUrl + path },
      { hreflang: 'ar', href: `${this.baseUrl}/ar${path}` },
      { hreflang: 'x-default', href: this.baseUrl + path },
    ].forEach(({ hreflang, href }) => {
      const link = this.doc.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      this.doc.head.appendChild(link);
    });
  }

  private setOpenGraphTags(title: string, description: string, url: string): void {
    const ogUrl = this.baseUrl + url;
    const locale = url.startsWith('/ar') ? 'ar_AE' : 'en_US';

    [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: ogUrl },
      { property: 'og:image', content: this.ogImage },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: locale },
    ].forEach(tag => this.metaService.updateTag(tag));

    [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: this.ogImage },
      { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
      { name: 'author', content: 'Al Qaryah Auction' },
      { name: 'publisher', content: 'Al Qaryah Auction' },
    ].forEach(tag => this.metaService.updateTag(tag));
  }
}
